/**
 * The client's activity, page by page, from `GET /clients/{id}/activity`.
 *
 * ONE HOOK FOR THE PAGES, ONE FUNCTION FOR THE INVALIDATION. The endpoint
 * pages by cursor (newest first, an exact page even though it is merged from
 * fifteen sources; see `activity_feed.py`), so the tab reads it through an
 * infinite query and shows a "Load more" while a cursor comes back. Every
 * screen that writes something the feed shows (a note, a meeting marked
 * held, a document filed, a reply sent) calls `invalidateClientActivity`,
 * which drops every period and every page of this client's feed at once by
 * matching the generated key's prefix.
 *
 * `since`/`until` are part of the key, so a period change is a new query and
 * a period the advisor comes back to is already cached.
 */
import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import {
  getListClientActivityQueryKey,
  listClientActivity,
} from '@biasafe.queries/clients-client-id-activity/clients-client-id-activity';
import type { ActivityItem, ActivityResponse } from '@biasafe.models';

/** The server's page; the tab's "Load more" fetches the next one. */
export const ACTIVITY_PAGE_SIZE = 50;

export interface ActivityWindow {
  /** ISO instants; the server reads them inclusive/exclusive as documented. */
  since?: string | null;
  until?: string | null;
}

export function activityQueryKey(clientId: string, window: ActivityWindow) {
  return [
    ...getListClientActivityQueryKey(clientId, {
      since: window.since ?? null,
      until: window.until ?? null,
    }),
    'infinite',
  ] as const;
}

export function useClientActivity(
  clientId: string,
  window: ActivityWindow = {},
  enabled = true,
) {
  const query = useInfiniteQuery({
    queryKey: activityQueryKey(clientId, window),
    queryFn: ({ pageParam, signal }) =>
      listClientActivity(
        clientId,
        {
          since: window.since ?? null,
          until: window.until ?? null,
          cursor: pageParam ?? null,
          limit: ACTIVITY_PAGE_SIZE,
        },
        undefined,
        signal,
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: ActivityResponse) => last.nextCursor ?? undefined,
    enabled: enabled && !!clientId,
  });

  const pages = query.data?.pages;
  const items = useMemo<ActivityItem[]>(
    () => pages?.flatMap((p) => p.items) ?? [],
    [pages],
  );
  // The union across pages: a source that failed on page two is still a
  // source that failed, and the banner says so once.
  const sourcesFailed = useMemo<string[]>(
    () => [...new Set(pages?.flatMap((p) => p.sourcesFailed ?? []) ?? [])],
    [pages],
  );

  return {
    items,
    sourcesFailed,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => void query.refetch(),
    hasMore: query.hasNextPage,
    isLoadingMore: query.isFetchingNextPage,
    loadMore: () => void query.fetchNextPage(),
  };
}

/**
 * Drop every cached page of this client's feed, whatever the period.
 *
 * Matched by the generated key's PREFIX, which is how react-query matches by
 * default: `['/clients/{id}/activity']` covers `[..., params, 'infinite']`
 * for every `params` the tab has asked with.
 */
export function invalidateClientActivity(
  queryClient: QueryClient,
  clientId: string,
) {
  return queryClient.invalidateQueries({
    queryKey: getListClientActivityQueryKey(clientId),
  });
}
